/* ═══════════════════════════════════════════════════════════════════════════
   AUVA VITALITY — auva.js  (Shopify OS 2.0 port of the Phase-1 main.js)
   Vanilla JS. GSAP + ScrollTrigger + MotionPath + ScrollTo (+ Lenis desktop).
   GSAP BEHAVIOR IS UNCHANGED from the static build. The only difference vs
   main.js: the old AUVA_CONFIG data-rendering (product cards, stat strip,
   marquee items, jar chips, text/href binding) is gone — Liquid now renders
   that markup server-side. These init functions only wire behavior/animation
   onto the Liquid-rendered DOM.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  "use strict";

  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const HAS_GSAP = typeof window.gsap !== "undefined";

  if (HAS_GSAP && window.ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
    if (window.MotionPathPlugin) gsap.registerPlugin(MotionPathPlugin);
    if (window.ScrollToPlugin) gsap.registerPlugin(ScrollToPlugin);
  }

  let lenisInstance = null; // set by initLenis (desktop only)
  let revealSweep = null; // set by initReveals; reveals content at/above viewport

  /* ═══ SECTION: announcement-bar ═══
     Liquid renders the first line into [data-announcement] and, when there is
     more than one line, a JSON array of all lines into data-lines. */
  function initAnnouncement() {
    const el = $("[data-announcement]");
    if (!el) return;
    let lines = [];
    try { lines = JSON.parse(el.getAttribute("data-lines") || "[]"); } catch (_) {}
    if (lines.length > 1 && !REDUCED) {
      let i = 0;
      el.style.transition = "opacity 0.3s";
      setInterval(() => {
        i = (i + 1) % lines.length;
        el.style.opacity = 0;
        setTimeout(() => {
          el.textContent = lines[i];
          el.style.opacity = 1;
        }, 300);
      }, 4500);
    }
  }

  /* ═══ SECTION: nav ═══ */
  function initNav() {
    const nav = $("#site-nav");
    if (!nav) return;
    const onScroll = () => nav.classList.toggle("is-scrolled", window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    const burger = $("#nav-burger");
    const links = $("#nav-links");
    if (burger && links) {
      burger.addEventListener("click", () => {
        const open = links.classList.toggle("is-open");
        burger.setAttribute("aria-expanded", String(open));
        burger.setAttribute("aria-label", open ? "Close menu" : "Open menu");
      });
      links.addEventListener("click", (e) => {
        if (e.target.closest("a")) {
          links.classList.remove("is-open");
          burger.setAttribute("aria-expanded", "false");
        }
      });
    }
  }

  /* ═══ SECTION: hero ═══ (stats are Liquid-rendered; this only animates) */
  function initHero() {
    const hero = $(".hero");
    if (!hero) return;
    if (!HAS_GSAP || REDUCED) return;

    // Underline swash draws itself in
    const swash = $(".accent-swash .swash path");
    if (swash) {
      const len = swash.getTotalLength();
      gsap.set(swash, { strokeDasharray: len, strokeDashoffset: len });
      gsap.to(swash, { strokeDashoffset: 0, duration: 1, delay: 0.5, ease: "power2.out" });
    }

    // Floating gummy accents drift gently
    $$(".hero-float").forEach((g, i) => {
      gsap.to(g, {
        y: i % 2 ? 14 : -16,
        rotation: i % 2 ? -7 : 6,
        duration: 2.6 + i * 0.5,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    });
  }

  /* ═══ SECTION: trust-marquee ═══
     Liquid renders one sequence into [data-marquee-seq]; JS clones it to fill
     the viewport and mirrors it for a seamless -50% loop. */
  function initMarquee() {
    const track = $("[data-marquee]");
    const seqHost = $("[data-marquee-seq]");
    if (!track || !seqHost) return;
    if (REDUCED) return; // static wrap fallback handled in CSS

    const half = seqHost.outerHTML;
    const probe = document.createElement("div");
    probe.style.cssText = "position:absolute;visibility:hidden;white-space:nowrap;";
    probe.innerHTML = half;
    document.body.appendChild(probe);
    const seqW = probe.firstElementChild.getBoundingClientRect().width || 600;
    probe.remove();
    const repeats = Math.max(1, Math.ceil((window.innerWidth * 1.2) / seqW));
    const halfHTML = new Array(repeats).fill(half).join("");
    track.innerHTML = halfHTML + halfHTML;
  }

  /* ═══ SECTION: jar-scroll ═══ (chips are Liquid-rendered; timeline unchanged) */
  function initJarScroll() {
    const section = $(".jarscroll");
    if (!section) return;
    if (!HAS_GSAP || !window.ScrollTrigger || REDUCED) return; // static composed fallback

    const words = $$(".jar-w", section);
    const chips = $$(".jar-chip", section);

    gsap.set(words, { yPercent: 110, autoAlpha: 0 });
    gsap.set(chips, { autoAlpha: 0, y: 26, scale: 0.85 });
    gsap.set(".jar-gummy", { autoAlpha: 0, scale: 0.4 });

    const mm = gsap.matchMedia();
    mm.add(
      { isDesktop: "(min-width: 900px)", isMobile: "(max-width: 899.98px)" },
      (ctx) => {
        const { isDesktop } = ctx.conditions;
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: "top top",
            end: isDesktop ? "+=200%" : "+=150%", // pin length
            scrub: 1,
            pin: true,
            anticipatePin: 1,
          },
        });

        // Headline words stagger in first
        tl.to(words, { yPercent: 0, autoAlpha: 1, stagger: 0.05, duration: 0.28, ease: "power3.out" }, 0.02);

        // Magenta glow builds through the scrub
        tl.to(".jarscroll-glow", { opacity: 1, duration: 0.9, ease: "none" }, 0.05);

        // Jar scales up and tilts
        tl.fromTo(
          ".jar-body",
          { scale: 0.9, rotation: 0 },
          { scale: 1.15, rotation: -8, duration: 0.75, ease: "power1.inOut" },
          0.15
        );

        // Cap lifts, rotates away and tosses off out of frame (fades so the
        // settle frame stays clean around the headline)
        tl.to(
          ".jar-cap",
          { yPercent: -260, xPercent: 120, rotation: 38, autoAlpha: 0, duration: 0.45, ease: "power2.in" },
          0.2
        );

        // Exactly TWO gummies arc up and out of the jar mouth (brand rule: 2/day)
        const arc = (target, dir, pos) => {
          tl.to(target, { autoAlpha: 1, scale: 1, duration: 0.07 }, pos);
          tl.to(
            target,
            {
              keyframes: [
                { yPercent: -170, xPercent: 55 * dir, rotation: 95 * dir, duration: 0.32, ease: "power2.out" },
                { yPercent: 55, xPercent: 215 * dir, rotation: 225 * dir, duration: 0.3, ease: "power1.in" },
                { yPercent: 42, xPercent: 228 * dir, rotation: 235 * dir, duration: 0.08, ease: "power1.out" },
              ],
            },
            pos + 0.02
          );
        };
        arc(".jar-gummy-1", -1, 0.3);
        arc(".jar-gummy-2", 1, 0.4);

        // Stat chips pop in, staggered
        tl.to(
          chips,
          { autoAlpha: 1, y: 0, scale: 1, stagger: 0.11, duration: 0.22, ease: "back.out(2.2)" },
          0.68
        );

        // Small settle beat at the end so the last frame breathes
        tl.to({}, { duration: 0.12 });
      }
    );
  }

  /* ═══ SECTION: buy-box ═══ */
  function initBuyBox() {
    $$(".buybox").forEach((box) => {
      const mainImg = $(".buybox-main img", box);
      $$(".buybox-thumb", box).forEach((btn) => {
        btn.addEventListener("click", () => {
          if (!mainImg) return;
          mainImg.src = btn.dataset.gallerySrc;
          // The gallery now renders product.media, which carries real alt text.
          if (btn.dataset.galleryAlt) mainImg.alt = btn.dataset.galleryAlt;
          $$(".buybox-thumb", box).forEach((b) => b.classList.remove("is-active"));
          btn.classList.add("is-active");
        });
      });
      // The 1 / 2 / 3 pack ladder needs NO JavaScript: the radios are named
      // "quantity", so the checked one is submitted as the cart quantity, and
      // the selected-card styling is pure CSS (:has(input:checked)). Leave it
      // that way — it keeps the buy path working if this file fails to load.
    });
  }

  /* ── Shared: accordions (buy box + FAQ) ──────────────────────────────── */
  function initAccordions() {
    $$(".accordion-item").forEach((item) => {
      const summary = $("summary", item);
      const body = $(".accordion-body", item);
      if (!summary || !body) return;

      if (!HAS_GSAP || REDUCED) return; // native <details> behavior

      summary.addEventListener("click", (e) => {
        e.preventDefault();
        gsap.set(body, { overflow: "hidden" });
        if (item.open) {
          gsap.to(body, {
            height: 0,
            opacity: 0,
            duration: 0.32,
            ease: "power2.inOut",
            onComplete: () => {
              item.open = false;
              gsap.set(body, { clearProps: "all" });
            },
          });
        } else {
          item.open = true;
          gsap.from(body, {
            height: 0,
            opacity: 0,
            duration: 0.4,
            ease: "power2.out",
            onComplete: () => gsap.set(body, { clearProps: "all" }),
          });
        }
      });
    });
  }

  /* ═══ SECTION: final-cta (email capture) ═══
     The form now POSTS to Shopify ({% form 'customer' %}), so this must not
     intercept it. The previous version called preventDefault(), wrote the address
     to localStorage and faked a success message — the customer was told they had
     subscribed while the address went nowhere the business could reach.

     Nothing left to wire: Shopify handles the submit, the redirect and the
     success/error state server-side. No storage APIs are used anywhere in this
     file. Kept as a named no-op so boot() reads the same and the section has an
     obvious place to hang future behaviour. */
  function initEmailForms() {
    // Intentionally empty — see above. Do not re-add a submit handler here.
  }

  /* ── Shared: reveal-on-scroll ────────────────────────────────────────── */
  function initReveals() {
    if (!HAS_GSAP || !window.ScrollTrigger || REDUCED) return;
    const targets = $$("[data-reveal]");
    if (!targets.length) return;

    const show = (batch, instant) => {
      const els = batch.filter((el) => !el.dataset.revealed);
      if (!els.length) return;
      els.forEach((el) => (el.dataset.revealed = "1"));
      if (instant) {
        // Synchronous — content skipped by an anchor jump must never stay hidden
        gsap.set(els, { autoAlpha: 1, y: 0, overwrite: true });
        return;
      }
      gsap.to(els, {
        autoAlpha: 1,
        y: 0,
        duration: 0.75,
        stagger: 0.09,
        ease: "power3.out",
        overwrite: true,
      });
    };

    gsap.set(targets, { autoAlpha: 0, y: 26 });
    ScrollTrigger.batch(targets, {
      start: "top 88%",
      onEnter: (b) => show(b),
      onEnterBack: (b) => show(b), // scrolling back up past skipped content
    });

    // Safety sweep: anchor jumps (e.g. hero → #buy) can skip elements entirely.
    // After any scroll settles, reveal whatever sits at or above the viewport.
    const sweep = () => {
      const limit = window.innerHeight * 0.92;
      show(targets.filter((el) => el.getBoundingClientRect().top < limit), true);
    };
    revealSweep = sweep;
    ScrollTrigger.addEventListener("scrollEnd", sweep);
    let sweepTimer;
    window.addEventListener(
      "scroll",
      () => {
        clearTimeout(sweepTimer);
        sweepTimer = setTimeout(sweep, 160);
      },
      { passive: true }
    );
    window.addEventListener("load", () => {
      ScrollTrigger.refresh();
      sweep();
    });
  }

  /* ── Shared: Lenis smooth scroll (desktop, fine pointer, motion OK) ───── */
  function initLenis() {
    if (
      typeof window.Lenis === "undefined" ||
      REDUCED ||
      !window.matchMedia("(pointer: fine)").matches ||
      window.innerWidth < 900 ||
      !HAS_GSAP
    )
      return;

    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    lenisInstance = lenis;
  }

  /* ── Shared: same-page anchor scrolling ──────────────────────────────────
     Native smooth scrolls get cancelled when they cross a ScrollTrigger pin,
     so anchors are driven by Lenis (desktop) or GSAP ScrollToPlugin. */
  function initAnchorScroll() {
    document.addEventListener("click", (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const hash = a.getAttribute("href");
      if (hash === "#" || hash.length < 2) return;
      const target = document.querySelector(hash);
      if (!target) return;
      if (REDUCED || !HAS_GSAP) return; // native instant jump (scroll-margin-top)
      e.preventDefault();
      const done = () => revealSweep && revealSweep();
      if (lenisInstance) {
        lenisInstance.scrollTo(target, { offset: -70, onComplete: done });
      } else if (window.ScrollToPlugin) {
        gsap.to(window, {
          duration: 0.9,
          ease: "power2.inOut",
          scrollTo: { y: target, offsetY: 70, autoKill: false },
          overwrite: true,
          onComplete: done,
        });
      } else {
        target.scrollIntoView(); // instant fallback
        done();
      }
    });
  }

  /* ═══ Judge.me — reveal only when there is something real to show ═══
     There are ZERO reviews today and a seeding campaign starts shortly. The
     star badge and the review wall both ship `hidden` from Liquid; this is the
     ONLY thing that unhides them, and only on hard evidence:

       badge → a .jdgm-prev-badge carrying data-number-of-reviews >= 1
       wall  → at least one rendered .jdgm-rev (an actual review)

     So an empty widget stays invisible instead of rendering a 0.0-star block or
     an empty grey rail. Judge.me writes asynchronously, hence the observer; the
     timeout is the backstop for the case where its script never arrives (today:
     the app is installed but its theme APP EMBED is not enabled, so no Judge.me
     JavaScript reaches the page at all and both stay correctly hidden).
     No storage APIs — nothing is persisted. */
  function initJudgeMe() {
    const badges = $$("[data-judgeme-badge]");
    const walls = $$("[data-judgeme-wall]");
    if (!badges.length && !walls.length) return;

    const hasReviews = (el) => {
      const prev = $(".jdgm-prev-badge", el);
      if (prev) {
        const n = parseInt(prev.getAttribute("data-number-of-reviews"), 10);
        if (!isNaN(n)) return n > 0;
      }
      return !!$(".jdgm-rev", el);
    };

    const sync = () => {
      let settled = true;
      let revealed = false;
      [...badges, ...walls].forEach((el) => {
        if (hasReviews(el)) {
          if (el.hidden) {
            el.hidden = false;
            revealed = true;
          }
        } else {
          settled = false;
        }
      });
      // Un-hiding changes page height, so pinned ScrollTriggers need re-measuring.
      if (revealed && HAS_GSAP && window.ScrollTrigger) ScrollTrigger.refresh();
      return settled;
    };

    if (sync()) return;

    // NO TIMEOUT. An earlier version gave up after 12s, which looked fine against
    // an empty widget but silently broke the real case: Judge.me LAZY-LOADS the
    // review wall when it scrolls into view, which on this page is easily more
    // than 12s after load — so the wall would never have appeared once reviews
    // existed. The observer is scoped to two containers and only fires when
    // Judge.me writes into them, so leaving it connected costs nothing. It
    // disconnects itself once everything is revealed.
    const observer = new MutationObserver(() => {
      if (sync()) observer.disconnect();
    });
    [...badges, ...walls].forEach((el) =>
      observer.observe(el, { childList: true, subtree: true, attributes: true })
    );
  }

  /* ── Boot ────────────────────────────────────────────────────────────── */
  function boot() {
    initAnnouncement();
    initNav();
    initHero();
    initMarquee();
    initJarScroll();
    initBuyBox();
    initJudgeMe();
    initAccordions();
    initEmailForms();
    initReveals();
    initLenis();
    initAnchorScroll();

    window.addEventListener("load", () => {
      if (HAS_GSAP && window.ScrollTrigger) ScrollTrigger.refresh();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Shopify theme editor: re-init when a section is re-rendered
  document.addEventListener("shopify:section:load", () => {
    if (HAS_GSAP && window.ScrollTrigger) ScrollTrigger.refresh();
  });
})();
